import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Siren, ChevronDown, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_MENU } from "@/data/content";

export const scrollToSection = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const goToContact = (navigate, service) => {
  if (service) sessionStorage.setItem("ciber_service", service);
  navigate("/contacto");
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [mobileGroup, setMobileGroup] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setHovered(null);
    setMobileGroup(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (hovered === null) return;
    const onKey = (e) => e.key === "Escape" && setHovered(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hovered]);

  const solid = scrolled || location.pathname !== "/" || hovered !== null;
  const isActive = (g) =>
    location.pathname === g.to ||
    (g.to !== "/" && location.pathname.startsWith(`${g.to}/`)) ||
    g.items.some((it) => it.to.split("#")[0] === location.pathname && it.to.split("#")[0] !== "/");

  return (
    <header
      data-testid="main-navbar"
      onMouseLeave={() => setHovered(null)}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-[#14163C]/90 backdrop-blur-xl border-b border-[#3FE0F2]/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center group">
          <Logo size={46} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 h-full" data-testid="navbar-links" aria-label="Principal">
          {NAV_MENU.map((g, i) => {
            const active = isActive(g);
            const expanded = hovered === i;
            return (
              <div key={g.label} className="h-full flex items-center" onMouseEnter={() => setHovered(i)}>
                <button
                  type="button"
                  data-testid={`nav-group-${i}`}
                  aria-expanded={expanded}
                  aria-haspopup="true"
                  onClick={() => setHovered(expanded ? null : i)}
                  onFocus={() => setHovered(i)}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-[13px] transition-colors duration-300 ${
                    active || expanded ? "text-[#3FE0F2]" : "text-[#B9C3EC] hover:text-[#3FE0F2]"
                  } ${active ? "font-semibold" : ""}`}
                >
                  {g.label}
                  <ChevronDown size={14} className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
                </button>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-emergency-cta"
            onClick={() => goToContact(navigate, "He sufrido un ciberataque")}
            className="hidden xl:inline-flex items-center gap-2 text-xs font-mono-cyber uppercase tracking-wider text-[#FF6B6B] border border-[#FF4D4D]/40 rounded-full px-4 py-2 hover:bg-[#FF4D4D]/10 hover:border-[#FF4D4D]/70 transition-[background-color,border-color] duration-300"
          >
            <Siren size={14} />
            ¿Ataque en curso?
          </button>
          <button
            data-testid="nav-contact-cta"
            onClick={() => goToContact(navigate)}
            className="hidden md:inline-flex items-center text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-5 py-2.5 hover:bg-[#7FEBF8] hover:shadow-[0_0_24px_rgba(63,224,242,0.4)] transition-[background-color,box-shadow] duration-300"
          >
            Solicitar información
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#EEFBFF] p-2"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Panel desplegable de escritorio */}
      <AnimatePresence>
        {hovered !== null && NAV_MENU[hovered] && (
          <motion.div
            key="mega"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute inset-x-0 top-[72px] bg-[#14163C]/95 backdrop-blur-xl border-b border-[#3FE0F2]/15 shadow-[0_30px_60px_rgba(0,0,0,0.45)]"
            data-testid="nav-mega-panel"
          >
            {(() => {
              const g = NAV_MENU[hovered];
              const twoCols = g.items.length > 6;
              return (
                <div className="max-w-7xl mx-auto px-8 py-8 grid grid-cols-12 gap-8">
                  <Link to={g.to} className="col-span-4 group relative rounded-2xl overflow-hidden border border-[#3FE0F2]/20 min-h-[220px]">
                    <img src={g.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#14163C] via-[#14163C]/60 to-transparent" />
                    <div className="relative h-full flex flex-col justify-end p-6">
                      <span className="font-heading text-xl font-semibold text-[#EEFBFF]">{g.label}</span>
                      <span className="mt-1 text-sm text-[#B9C3EC]">{g.intro}</span>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#3FE0F2]">
                        Ver sección <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                  <ul className={`col-span-8 grid ${twoCols ? "grid-cols-2" : "grid-cols-1 max-w-xl"} gap-x-6 gap-y-1 content-start`}>
                    {g.items.map((it) => (
                      <li key={it.to}>
                        <Link
                          to={it.to}
                          className="block rounded-xl px-4 py-2.5 hover:bg-[#242764] focus:bg-[#242764] outline-none transition-colors group"
                        >
                          <span className="block text-sm font-semibold text-[#EEFBFF] group-hover:text-[#3FE0F2] transition-colors">{it.label}</span>
                          {it.desc && <span className="block text-xs text-[#94A4D0] mt-0.5">{it.desc}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menú móvil en acordeón */}
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-navigation"
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-y-auto max-h-[calc(100vh-72px)] bg-[#1B1E4E] border-b border-[#3FE0F2]/20"
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV_MENU.map((g, i) => {
                const expanded = mobileGroup === i;
                return (
                  <div key={g.label} className="border-b border-[#3FE0F2]/10">
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() => setMobileGroup(expanded ? null : i)}
                      className={`w-full flex items-center justify-between py-3.5 text-base ${isActive(g) ? "text-[#3FE0F2] font-semibold" : "text-[#EEFBFF]"}`}
                    >
                      {g.label}
                      <ChevronDown size={18} className={`text-[#3FE0F2] transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
                    </button>
                    {expanded && (
                      <ul className="pb-3 pl-3 flex flex-col gap-0.5">
                        {g.items.map((it) => (
                          <li key={it.to}>
                            <Link to={it.to} className="block py-2 text-sm text-[#B9C3EC] hover:text-[#3FE0F2]">
                              {it.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
              <button
                data-testid="nav-mobile-contact-cta"
                onClick={() => goToContact(navigate)}
                className="mt-5 text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-5 py-3 text-center"
              >
                Solicitar información
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
